import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import { jwtDecode } from "jwt-decode";
import { useTranslation } from 'react-i18next';
import {
  Layout,
  Tabs,
  Input,
  DatePicker,
  Table,
  Space,
  Tag,
  Popconfirm, 
  Button, 
  Card,
  Typography,
  Select
} from 'antd';
import { DeleteOutlined, FileExcelOutlined, CheckSquareOutlined } from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import TeacherRatingReport from './TeacherRatingReport';
import TestScore from './TestScore';
import StudentFeedbackReport from './StudentFeedbackReport';
import StudentClassesReminder from '../Student/StudentClassesReminder';
import AdminLeaveManagement from '../Student/AdminLeaveManagement';
import CompletedStudentsTracker from '../Student/CompletedStudentsTracker';
import dayjs from 'dayjs';

import './Registrations.css';

const { Content } = Layout;
const { Title } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

const RegistrationList = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const startOfMonth = dayjs().startOf('month');
  const endOfMonth = dayjs().endOf('month');
  const [dateRange, setDateRange] = useState([startOfMonth, endOfMonth]);
  const [fromDate, setFromDate] = useState(startOfMonth.toDate());
  const [toDate, setToDate] = useState(endOfMonth.toDate());

  const [currentPage, setCurrentPage] = useState(1);

  const [itemsPerPage, setItemsPerPage] = useState(150);
  const [activeTab, setActiveTab] = useState('attendance');
  const { t } = useTranslation();

  const token = localStorage.getItem("token");
        let userRole = null;
        let userBranchId = null;
    
        if (token) {
        try {
            const decoded = jwtDecode(token);
            userRole = decoded.role;
            // Support both formats just in case
            userBranchId = decoded.branchId || decoded.branch_id || null;
        } catch (err) {
            console.error("Invalid token", err);
        }
        }
  const API_URL = process.env.REACT_APP_API_URL;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid date';
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    }).toUpperCase();
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid date';
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    }).toUpperCase() + ' ' + 
    date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };
  useEffect(() => {
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to fetch branches", err);
    }
  };

  fetchBranches();
}, [API_URL]);

 const fetchAttendanceData = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await axios.get(`${API_URL}/attendance`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const sortedAttendance = response.data.sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );


    setAttendanceData(sortedAttendance);
    setFilteredClasses(sortedAttendance);
  } catch (error) {
    console.error("❌ Error fetching attendance data:", error);
  }
}, [API_URL]);

useEffect(() => {
  fetchAttendanceData();
}, [fetchAttendanceData]);

  

  useEffect(() => {
    fetchAttendanceData();
  }, [fetchAttendanceData]);

useEffect(() => {
  let filtered = attendanceData.filter((attendance) => {
    const studentName =
      (attendance.student_firstname || '') + ' ' + (attendance.student_nickname || '');
    const matchesStudent = studentName
      .toLowerCase()
      .includes(studentSearchTerm.toLowerCase());

    const matchesCourse = attendance.class_code
      ?.toLowerCase()
      .includes(courseSearchTerm.toLowerCase());

    const matchesBranch =
      !selectedBranch || attendance.branch_id === selectedBranch;

    // ✅ Must match branch + student + course
    return matchesStudent && matchesCourse && matchesBranch;
  });

  if (fromDate && toDate) {
    filtered = filtered.filter((attendance) => {
      const attendanceDate = new Date(attendance.date);
      return attendanceDate >= fromDate && attendanceDate <= toDate;
    });
  }

  setFilteredClasses(filtered);
  setCurrentPage(1);
}, [studentSearchTerm, courseSearchTerm, attendanceData, fromDate, toDate, selectedBranch]);


  const handleDeleteAttendance = async (attendanceId) => {
  try {
    const token = localStorage.getItem('token');
    await axios.delete(`${API_URL}/attendance/${attendanceId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchAttendanceData(); // Refresh the data
  } catch (err) {
    console.error('Failed to delete attendance record:', err);
  }
};

  

  const currentItems = filteredClasses.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

     const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", 
};

  const columns = [
    {
        title: t("Branch"),
        key: "branch",
        dataIndex: "branch_name", // ✅ directly map branch_name
        width: 120,
        ellipsis: false,
        render: (_, record) => {
          const branchId = record.branch_id;
          const color = branchColors[branchId] || "default";
          return record.branch_name ? (
            <Tag color={color}>{record.branch_name}</Tag>
          ) : (
            <Tag>-</Tag>
          );
        },
      },
        {
      title: t('date'),
      dataIndex: 'date',
      width: 120,
      key: 'date',
      render: (date) => formatDate(date),
    },
    {
  title: t('time'),
  key: 'time_range',
  width: '150px',
  render: (_, record) => {
    const startTime = record.schedule_time
      ? record.schedule_time.slice(0, 5)
      : '-';

    const endTime = record.end_time || '-';
    const duration = record.schedule_duration || '';

    return (
      <div style={{ lineHeight: '1.4' }}>
        <div style={{ fontWeight: 500 }}>
          {startTime}–{endTime}
        </div>
        {duration && (
          <div style={{ fontSize: '12px', color: '#888' }}>
            ({duration})
          </div>
        )}
      </div>
    );
  },
},

      {
        title: t('student'),
        key: 'student',
        render: (_, record) => (
          <span>
            {record.student_firstname || ''}
            {record.student_nickname && (
              <span style={{ marginLeft: '8px', color: '#888' }}>
                ({record.student_nickname})
              </span>
            )}
          </span>
        ),
        ellipsis: false,       
        width: '200px',         
      },

      {
        title: t('course'),
        dataIndex: 'class_code',
        key: 'class_code',
        ellipsis: false,       
        width: 'auto',        
        render: (text) => (
          <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {text}
          </span>
        ),
        width: '200px'
      },

    { title: t('classid'), dataIndex: 'classid', key: 'classid', width: '100px' },
    { title: t('subject'), dataIndex: 'subject_name', width: '200px', ellipsis: false, key: 'subject_name' },
    { 
      title: t('teacher'), 
      dataIndex: 'teacher_nickname', 
      key: 'teacher_nickname',
      render: (teacher_nickname) => teacher_nickname || '-',
      width: '100px'
    },
    {
      title: t('type'),
      dataIndex: 'isprivate',
      key: 'isprivate',
      render: (isprivate) => (isprivate ? t('private') : t('group')),
      width: '100px'
    },

    {
      title: t('attendance'),
      dataIndex: 'status',
      key: 'status',
      width: '150px',
      render: (status) => (
        <Tag color={
          status === 'Present' ? 'green' :
          status === 'Absent' ? 'red' :
          status === 'To be rescheduled' ? 'purple' :
          status === 'Late' ? 'orange' : 'default'
        }>
          {status}
        </Tag>
      ),
    },
    {
      title: t('homework'),
      dataIndex: 'homework',
      key: 'homework',
      width: '120px',
      render: (homework) => (
        <Tag color={
          homework === 'YES' ? 'green' : 
          homework === 'NO' ? 'red' : 
          'default'
        }>
          {homework || 'N/A'}
        </Tag>
      ),
    },
    { 
      title: t('comments'), 
      dataIndex: 'comment', 
      key: 'comment',
      width: 250,
      render: (text) => (
        <div 
          style={{ 
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            minWidth: '150px',
            maxWidth: '250px',
            lineHeight: '1.5'
          }}
        >
          {text || '-'}
        </div>
      )
    },
    {
      title: t('timestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: '180px',
      render: (timestamp) => formatDateTime(timestamp)
    },
    {
      title: t('Last Edited'),
      dataIndex: 'edited_at',
      key: 'edited_at',
      width: '180px',
      render: (edited_at) => formatDateTime(edited_at)
    },

    {
  title: t('delete'),
  key: 'actions',
  fixed: 'right',
  width: '90px',
  render: (_, record) => (
    <Popconfirm
      title={t('Are you sure to delete this attendance record?')}
      onConfirm={() => handleDeleteAttendance(record.attendance_id)}
      okText={t('Yes')}
      cancelText={t('No')}
    >
      <Button type="primary" danger icon={<DeleteOutlined />} size="small">
      </Button>
    </Popconfirm>
  )
}

];

// Update the CSV data mapping
const csvData = currentItems.map((attendance) => ({
  branch: attendance.branch_name || '-',
  date: formatDate(attendance.date),

  start_time: attendance.schedule_time
    ? attendance.schedule_time.slice(0, 5)
    : '-',
  end_time: attendance.end_time || '-',
  duration: attendance.schedule_duration || '-',

  student_firstname: attendance.student_firstname,
  student_nickname: attendance.student_nickname,
  course: attendance.class_code,
  classid: attendance.classid,
  subject: attendance.subject_name,
  teacher: attendance.teacher_nickname || '-',
  type: attendance.isprivate ? t('private') : t('group'),
  attendance: attendance.status,
  homework: attendance.homework || 'N/A',
  comments: attendance.comment,
  timestamp: formatDateTime(attendance.timestamp),
  lastEdited: formatDateTime(attendance.edited_at),
}));


  return (
    <Layout>
      <Sidebar />
        <Layout>
          <Header />
            <Content style={{ marginTop: '60px', marginLeft: '70px', padding: '24px' }}>
              <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab={t('Attendance')} key="attendance">
                  <Card style={{ marginBottom: 16 }}>
                    <Title level={3}>
                      <CheckSquareOutlined  /> {t('Student Attendance')}</Title>
                      {userRole === "superadmin" && (
                      <Select
                        placeholder="Select Branch"
                        value={selectedBranch}
                        onChange={(value) => setSelectedBranch(value)}
                        style={{ width: 200, marginBottom: 16, marginRight: 12 }}
                        allowClear
                      >
                        {branches.map((branch) => (
                          <Option key={branch.id} value={branch.id}>
                            {branch.name}
                          </Option>
                        ))}
                      </Select>
                    )}

                  <Space style={{ marginBottom: 16 }} wrap>
                      <Input
                        placeholder={t('Search By Student Name')}
                        value={studentSearchTerm}
                        onChange={(e) => setStudentSearchTerm(e.target.value)}
                        style={{ width: 200 }}
                        allowClear 
                      />
                      <Input
                        placeholder={t('Search By Course')}
                        value={courseSearchTerm}
                        onChange={(e) => setCourseSearchTerm(e.target.value)}
                        style={{ width: 200 }}
                        allowClear
                      />
                      <RangePicker
                        value={dateRange}
                        onChange={(dates) => {
                          setDateRange(dates);
                          setFromDate(dates ? dates[0].toDate() : null);
                          setToDate(dates ? dates[1].toDate() : null);
                        }}
                        allowClear
                      />
                    <CSVLink 
                      data={csvData} 
                      filename="attendance_data.csv" 
                      className="ant-btn"
                    >
                      <Button><FileExcelOutlined style={{ marginRight: 6 }} /> {t('Export CSV')}</Button>
                    </CSVLink>
                    </Space>

                    <Table
                          columns={columns}
                          dataSource={currentItems}
                          rowKey={(record) => record.attendance_id}
                          scroll={{ 
                            x: 1800, 
                          }}
                          style={{
                            overflowX: 'auto',
                            width: '100%'
                          }}
                          pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showQuickJumper: true,
                       
                        }}
                      />
                  
                      </Card>
                  </TabPane>
                  <TabPane tab={t('Student Feedback')} key="studentFeedback">
                    <StudentFeedbackReport />
                  </TabPane>
                  <TabPane tab={t('Leave Requests')} key="leaveRequests">
                  <AdminLeaveManagement />
                </TabPane>
                  <TabPane tab={t('Course End Reminder')} key="courseEndReminder">
                    <StudentClassesReminder />
                  </TabPane>
                   <TabPane tab={t('Completed Student Tracker')} key="completedstudenttracker">
                    <CompletedStudentsTracker />
                  </TabPane>
                  <TabPane tab={t('Teacher Rating')} key="teacherRating">
                    <TeacherRatingReport />
                  </TabPane>
                  <TabPane tab={t('Student Test Scores')} key="testScore">
                    <TestScore />
                  </TabPane>
                </Tabs>
              </Content>
            </Layout>
          </Layout>
        );
      };

export default withRoleAccess(RegistrationList, ['admin', 'superadmin']);
